import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminCourses() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);

  const [form, setForm] = useState({
    id: null,
    course_code: "",
    name: "",
    base_fee: "",
    sort_order: 0,
    is_active: 1,
  });

  const isEditing = form.id != null;

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/admin/courses", {
        params: {
          q: opts.search !== undefined ? opts.search : search || undefined,
          include_inactive: includeInactive ? "1" : undefined,
        },
      });
      setItems(data);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      id: null,
      course_code: "",
      name: "",
      base_fee: "",
      sort_order: 0,
      is_active: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }
    const feeNum = Number(form.base_fee);
    if (!Number.isFinite(feeNum) || feeNum < 0) {
      alert("Fee must be a non-negative number");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        course_code: form.course_code || null,
        name: form.name.trim(),
        base_fee: feeNum,
        sort_order: form.sort_order != null ? Number(form.sort_order) : 0,
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await api.put(`/api/admin/courses/${form.id}`, payload);
      } else {
        await api.post("/api/admin/courses", payload);
      }

      resetForm();
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save course");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      course_code: item.course_code || "",
      name: item.name || "",
      base_fee: item.base_fee != null ? String(item.base_fee) : "",
      sort_order: item.sort_order != null ? item.sort_order : 0,
      is_active: item.is_active ? 1 : 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Mark this course as inactive?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/admin/courses/${id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete course");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f8fafc" },
    container: { maxWidth: 960, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    buttonPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    buttonGhost: {
      background: "#e2e8f0",
      color: "#0f172a",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      fontSize: 12,
      border: `1px solid ${active ? "#0f766e" : "#dc2626"}`,
      color: active ? "#0f766e" : "#dc2626",
      background: active ? "#E7F8F1" : "#FDECEC",
    }),
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Course Master</h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Form */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={styles.label}>Course Name *</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Yoga Level 1"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Course Code (optional)</label>
                <input
                  style={styles.input}
                  value={form.course_code}
                  onChange={(e) => setForm({ ...form, course_code: e.target.value })}
                  placeholder="YOGA-101"
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <div>
                <label style={styles.label}>Base Fee (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={form.base_fee}
                  onChange={(e) => setForm({ ...form, base_fee: e.target.value })}
                  placeholder="1500"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Sort Order</label>
                <input
                  type="number"
                  style={styles.input}
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm({ ...form, sort_order: e.target.value === "" ? 0 : Number(e.target.value) })
                  }
                />
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                id="course-active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
                }
              />
              <label htmlFor="course-active" style={{ fontSize: 14 }}>
                Active
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button type="submit" style={styles.buttonPrimary} disabled={loading}>
                {isEditing ? "Update Course" : "Add Course"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <input
              style={{ ...styles.input, maxWidth: 260 }}
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              style={styles.buttonGhost}
              onClick={() => loadData({ search })}
            >
              Search
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  setTimeout(() => loadData({}), 0);
                }}
              />
              Show inactive
            </label>
          </div>

          {loading && <div>Loading...</div>}
          {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}
          {!loading && !items.length && <div>No courses found.</div>}

          {!!items.length && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: 8, textAlign: "left" }}>Name</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Code</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Base Fee (₹)</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Sort</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 8 }}>{item.name}</td>
                      <td style={{ padding: 8 }}>{item.course_code || "-"}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        {item.base_fee != null ? item.base_fee.toFixed(2) : "-"}
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>{item.sort_order}</td>
                      <td style={{ padding: 8 }}>
                        <span style={styles.pill(item.is_active)}>
                          {item.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: 8, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          style={{ ...styles.buttonGhost, padding: "4px 10px", marginRight: 6 }}
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={{ ...styles.buttonGhost, padding: "4px 10px", background: "#fee2e2", border: "1px solid #fecaca" }}
                          onClick={() => handleDelete(item.id)}
                          disabled={!item.is_active}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
